import { Button } from 'antd';
import React from 'react';
import {
  Link,
  ButtonGroup,
  LoadingState,
  PrimaryButton,
} from '@common/components';

import { AcceptApprovalChainStepMutation } from '@approvals/graphql';
import { useMutation } from '@common/hooks';
import { buildDialogUrl } from '@common/services';
import { useCurrentMembership } from '@identity/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';

type ApprovalChainActionButtonsProps = {
  approvalChainStepId?: string;
  approveButtonText: string;
  currentApprovers?: {
    id: string;
    worksMembership: {
      id: string;
      name: string;
    };
  }[];
  rejectionDrawerTitle: string;
  extraApprovalButton?: React.ReactNode;
  onCompleted?: () => void;
};

export const ApprovalChainActionButtons: React.FC<
  ApprovalChainActionButtonsProps
> = ({
  approvalChainStepId,
  approveButtonText,
  currentApprovers,
  rejectionDrawerTitle,
  onCompleted,
  extraApprovalButton,
}) => {
  const currentMembership = useCurrentMembership();

  const [acceptStep, { loading: approvingLoading }] = useMutation(
    AcceptApprovalChainStepMutation,
    {
      onCompleted() {
        createMessage('success', 'This level has been successfully approved.');
        onCompleted?.();
      },
      onError: () => {
        createMessage(
          'error',
          'Approval Chain Step could not be approved. Please try again or contact your hiring partner.'
        );
      },
      refetchQueries: [
        'StaffingRequestPageQuery',
        'CandidateAssignmentQuery',
        'ApprovalChainsPendingActionListQuery',
      ],
    }
  );

  if (!currentMembership) {
    return <LoadingState />;
  }

  const onApproveClick = () => {
    acceptStep({
      variables: {
        approvalChainStepId: approvalChainStepId,
        worksMembershipId: currentMembership.id,
      },
    });
  };

  const canOverrideApprove =
    currentMembership.canApprovalsOverrideApprove || false;
  const canOverrideReject =
    currentMembership.canApprovalsOverrideReject || false;

  const isCurrentApprover = currentApprovers?.find(
    (approver) => currentMembership.id === approver?.worksMembership.id
  );

  const rejectApprovalChainDrawerUrl = approvalChainStepId
    ? buildDialogUrl('approvals-chain-reject-drawer', {
        props: {
          approvalChainStepId,
          worksMembershipId: currentMembership.id,
          rejectionDrawerTitle,
        },
      })
    : '';

  return (
    <>
      {approvalChainStepId && (
        <>
          <ButtonGroup>
            {(isCurrentApprover || canOverrideReject) && (
              <Link to={rejectApprovalChainDrawerUrl}>
                <Button danger size="large" type="default">
                  Reject
                </Button>
              </Link>
            )}
            {(isCurrentApprover || canOverrideApprove) && (
              <>
                {extraApprovalButton}
                <PrimaryButton
                  loading={approvingLoading}
                  onClick={onApproveClick}
                  size="large"
                >
                  {approveButtonText}
                </PrimaryButton>
              </>
            )}
          </ButtonGroup>
        </>
      )}
    </>
  );
};
